/**
 * Registration Page
 * Self-registration form for new members
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUIStore } from '@/stores/uiStore';
import { apiCall } from '@/lib/api';
import { registrationSchema, type RegistrationData } from '../../server/lib/validation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LocationColorDot } from '@/components/ui/location-color-dot';
import bismillahSrc from '@/images/bismillah.png';
import type { PickupLocation } from '@/types/api';
import { RSVP_SIZES, RSVP_SIZE_LABELS, type RSVPSizeCode } from '@/types/units';

export function Register() {
  const [isLoading, setIsLoading] = useState(false);
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);

  const { showToast } = useUIStore();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<RegistrationData>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      itsId: '',
      name: '',
      phone: '',
      email: '',
      password: '',
      confirmPassword: '',
      pickupLocationId: 0,
      defaultSize: null,
    },
  });

  const selectedLocationId = watch('pickupLocationId');

  /**
   * Fetch pickup locations on mount
   */
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await apiCall<{ locations: PickupLocation[] }>('/api/pickup-locations');
        setPickupLocations(response.locations);
      } catch (error) {
        showToast('Failed to load pickup locations', 'error');
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [showToast]);

  /**
   * Handle form submission
   */
  const onSubmit = async (data: RegistrationData) => {
    setIsLoading(true);

    try {
      await apiCall('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      });

      showToast('Registration submitted successfully! Awaiting approval.', 'success');

      // Redirect to login after successful registration
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen warm-gradient-bg p-4">
      <div className="w-full max-w-2xl flex flex-col items-center">
        {/* Compact branding */}
        <div className="flex items-center gap-3 mb-6">
          <img
            src={bismillahSrc}
            alt="Bismillah"
            className="h-10 w-auto drop-shadow-sm dark-logo-filter"
          />
          <div>
            <p className="text-sm font-semibold text-stone-800 dark:text-stone-200 leading-tight">Faiz-ul-Mawaid al-Burhaniyah</p>
            <p className="text-xs text-stone-500 dark:text-stone-400 tracking-widest uppercase">San Jose</p>
          </div>
        </div>

        <Card className="w-full shadow-lg border-stone-200/60 dark:border-stone-700/60">
          <CardHeader>
            <CardTitle className="text-2xl">Member Registration</CardTitle>
            <CardDescription>
            Register for an account to reserve your thaali. Your account will require admin approval before you can log in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* ITS ID */}
            <div className="space-y-2">
              <Label htmlFor="itsId">
                ITS ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="itsId"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Enter your ITS ID"
                {...register('itsId')}
                disabled={isLoading}
                aria-invalid={!!errors.itsId}
              />
              {errors.itsId && (
                <p className="text-sm text-destructive">{errors.itsId.message}</p>
              )}
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Full Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter your full name"
                {...register('name')}
                disabled={isLoading}
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">
                Phone Number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="Enter your phone number"
                {...register('phone')}
                disabled={isLoading}
                aria-invalid={!!errors.phone}
              />
              {errors.phone && (
                <p className="text-sm text-destructive">{errors.phone.message}</p>
              )}
            </div>

            {/* Email (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="email">Email (Optional)</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                {...register('email')}
                disabled={isLoading}
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            {/* Pickup Location */}
            <div className="space-y-2">
              <Label htmlFor="pickupLocationId">
                Pickup Location <span className="text-destructive">*</span>
              </Label>
              <Select
                value={selectedLocationId > 0 ? selectedLocationId.toString() : undefined}
                onValueChange={(value) => setValue('pickupLocationId', parseInt(value, 10))}
                disabled={isLoading || isLoadingLocations}
              >
                <SelectTrigger id="pickupLocationId">
                  <SelectValue placeholder={isLoadingLocations ? 'Loading locations...' : 'Select a pickup location'} />
                </SelectTrigger>
                <SelectContent>
                  {pickupLocations.map((location) => (
                    <SelectItem key={location.id} value={location.id.toString()}>
                      <span className="flex items-center gap-2">
                        <LocationColorDot colorCode={location.colorCode} />
                        {location.name}
                      </span>
                      {location.address && (
                        <span className="text-xs text-muted-foreground ml-2">
                          ({location.address})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.pickupLocationId && (
                <p className="text-sm text-destructive">{errors.pickupLocationId.message}</p>
              )}
            </div>

            {/* Default Size (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="defaultSize">Default RSVP Size (Optional)</Label>
              <Select
                value={watch('defaultSize') || ''}
                onValueChange={(value) => setValue('defaultSize', value === '' ? null : (value as RSVPSizeCode))}
                disabled={isLoading}
              >
                <SelectTrigger id="defaultSize">
                  <SelectValue placeholder="Select default size" />
                </SelectTrigger>
                <SelectContent>
                  {RSVP_SIZES.map((size) => (
                    <SelectItem key={size} value={size}>
                      {RSVP_SIZE_LABELS[size]} ({size})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">
                Password <span className="text-destructive">*</span>
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password"
                {...register('password')}
                disabled={isLoading}
                aria-invalid={!!errors.password}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Must be at least 8 characters with one uppercase letter and one number
              </p>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                Confirm Password <span className="text-destructive">*</span>
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                {...register('confirmPassword')}
                disabled={isLoading}
                aria-invalid={!!errors.confirmPassword}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || isLoadingLocations}
            >
              {isLoading ? 'Submitting...' : 'Register'}
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Sign in here
              </Link>
            </p>
          </div>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
